/**
 * useSplitPreview — Story 2.3-UI (AC Live-Vorschau)
 *
 * Debounced Live-Vorschau ueber POST /api/v2/host_split_rules/preview:
 * Waehrend der Eingabe wird der Request-Body erst nach PREVIEW_DEBOUNCE_MS
 * Ruhe abgeschickt (kein Request pro Tastendruck). 422-Antworten (ZeroRate,
 * fehlender Wechselkurs) landen im error-State und werden im Editor als
 * BE-Meldung angezeigt — query-client.ts retryt 422 nicht.
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { operations } from '@heliumtracker/api-types'
import apiClient from '../../lib/api-client'

export type SplitPreviewRequest =
  operations['previewApiV2HostSplitRule']['requestBody']['content']['application/json']
export type SplitPreviewResult = NonNullable<
  operations['previewApiV2HostSplitRule']['responses'][200]['content']['application/json']['data']
>

/**
 * Antwort + die Annahmen, die sie erzeugt haben. Die Vorschau-Zeile darf den
 * Satz NUR aus diesem Paar bauen — sonst mischt sie waehrend Debounce/Fetch
 * eine alte Antwort mit neuen Formularwerten (aria-live liest den falschen
 * Satz vor).
 */
export type SplitPreviewSnapshot = {
  request: SplitPreviewRequest
  result: SplitPreviewResult
}

export const PREVIEW_DEBOUNCE_MS = 400

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

/**
 * request == null -> Vorschau deaktiviert (Formular noch unvollstaendig).
 * Der Key ist der serialisierte Body: gleiche Eingabe -> gecachte Antwort,
 * neue Eingabe -> neuer Request (nach Debounce).
 */
export function useSplitPreview(request: SplitPreviewRequest | null) {
  const serialized = request == null ? null : JSON.stringify(request)
  const debouncedKey = useDebouncedValue(serialized, PREVIEW_DEBOUNCE_MS)

  const query = useQuery({
    queryKey: ['split-rule-preview', debouncedKey],
    enabled: debouncedKey != null,
    staleTime: 30_000,
    // Vorherige Vorschau waehrend des Nachladens stehen lassen (kein Flackern).
    // Der Snapshot traegt seine eigenen Annahmen mit — die Zeile zeigt damit
    // immer einen in sich stimmigen Satz, auch wenn er noch der alte ist.
    placeholderData: (prev: SplitPreviewSnapshot | undefined) => prev,
    queryFn: async (): Promise<SplitPreviewSnapshot> => {
      const body = JSON.parse(debouncedKey as string) as SplitPreviewRequest
      const res = await apiClient.POST('/api/v2/host_split_rules/preview', { body })
      if (res.error) throw res.error
      // data.data kann laut Schema optional sein — dann als Fehler behandeln,
      // damit React Query nie undefined cached.
      const data = res.data?.data
      if (!data) throw new Error('preview response without data')
      return { request: body, result: data as SplitPreviewResult }
    },
  })

  // "Tippt noch": Eingabe hat sich geaendert, Debounce laeuft noch.
  const isDebouncing = serialized !== debouncedKey

  /**
   * Die angezeigte Antwort gehoert (noch) nicht zur aktuellen Eingabe:
   * Debounce laeuft, ein Request ist unterwegs, oder React Query zeigt die
   * vorherige Antwort als Platzhalter. Die Zeile rendert dann gedimmt +
   * aria-busy statt einen veralteten Satz als aktuelles Ergebnis auszugeben.
   */
  const isRecalculating = isDebouncing || query.isFetching || query.isPlaceholderData

  return { ...query, isDebouncing, isRecalculating }
}
